import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { API_URL, pathHomePage, pathLogin } from "../../config/constants";
import UserSearch from "../../components/userSearch";
import Logo from "../../assets/logo_eodyne.svg";
import {
  PageWrapper, Header, HeaderRight, UserInfo, RoleBadge, GhostButton,
  TitleRow, PageTitle, PrimaryButton, DangerButton,
  FormCard, FormRow, DateInput, ErrorMsg, EmptyState,
  CalendarNav, NavButton, MonthLabel,
  CalendarGrid, DayHeader, DayCell, DayNumber, EventChip, MoreChip,
  Overlay, DetailCard, DetailTitle, DetailRow, StatusBadge, ActionRow,
} from "./styles";

const STATUS_LABELS = {
  scheduled: "Programada",
  in_progress: "En curso",
  completed: "Completada",
  cancelled: "Cancelada",
  no_show: "No presentado",
};

const STATUS_COLORS = {
  scheduled: "#26A9B6",
  in_progress: "#4caf50",
  completed: "#888",
  cancelled: "#ff4d4d",
  no_show: "#ff9800",
};

const DAY_NAMES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const MAX_VISIBLE_EVENTS = 3;

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
};

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit" });
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const buildCalendarDays = (year, month) => {
  const first = new Date(year, month, 1);
  // Monday = 0, Sunday = 6
  let startOffset = (first.getDay() + 6) % 7;
  const startDate = new Date(year, month, 1 - startOffset);

  const days = [];
  const d = new Date(startDate);
  // Always show 6 rows (42 cells) for consistent height
  for (let i = 0; i < 42; i++) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
};

const Agenda = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formPatient, setFormPatient] = useState(null);
  const [formDate, setFormDate] = useState("");
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [dayDetailSessions, setDayDetailSessions] = useState(null);

  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const history = useHistory();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const isTherapist = currentUser.role === "therapist";

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    history.push(pathLogin);
  };

  const fetchSessions = () => {
    fetch(`${API_URL}/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => { setSessions(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchSessions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formPatient || !formDate) { setFormError("Selecciona un paciente y una fecha."); return; }
    setFormLoading(true);
    setFormError("");
    try {
      const res = await fetch(`${API_URL}/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ patient_id: formPatient.id, scheduled_at: formDate }),
      });
      if (!res.ok) {
        const d = await res.json();
        setFormError(d.error || "Error al crear la cita.");
        return;
      }
      setShowForm(false);
      setFormPatient(null);
      setFormDate("");
      fetchSessions();
    } catch {
      setFormError("Error de red.");
    } finally {
      setFormLoading(false);
    }
  };

  const cancelSession = async (id) => {
    await fetch(`${API_URL}/sessions/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: "cancelled" }),
    });
    setSelectedSession(null);
    fetchSessions();
  };

  const startSession = (session) => {
    const minutesUntil = (new Date(session.scheduled_at) - Date.now()) / 60000;
    if (minutesUntil > 15) {
      const scheduledStr = formatDate(session.scheduled_at);
      const ok = window.confirm(
        `La cita está programada para ${scheduledStr}.\n¿Deseas iniciarla ahora de todas formas?`
      );
      if (!ok) return;
    }
    history.push(pathHomePage, {
      sessionId: session.id,
      patient: {
        id: session.patient_id,
        first_name: session.patient_first_name,
        last_name: session.patient_last_name,
        role: "patient",
      },
    });
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const calendarDays = useMemo(() => buildCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);

  // Group sessions by date key "YYYY-MM-DD"
  const sessionsByDate = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      if (!s.scheduled_at) return;
      const d = new Date(s.scheduled_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    // Sort each day's sessions by time
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    );
    return map;
  }, [sessions]);

  const monthLabel = new Date(viewYear, viewMonth).toLocaleString("es-ES", { month: "long", year: "numeric" });

  const isOwner = (s) => s.therapist_id === currentUser.id;
  const otherName = (s) =>
    isOwner(s)
      ? `${s.patient_first_name} ${s.patient_last_name}`
      : `${s.therapist_first_name} ${s.therapist_last_name}`;

  return (
    <PageWrapper>
      <Header>
        <img src={Logo} alt="logo" style={{ width: "140px" }} />
        <HeaderRight>
          <UserInfo>
            {currentUser.first_name} {currentUser.last_name}
            <RoleBadge>{currentUser.role}</RoleBadge>
          </UserInfo>
          <GhostButton onClick={() => history.push(pathHomePage)}>Videollamada</GhostButton>
          <GhostButton onClick={logout}>Cerrar sesión</GhostButton>
        </HeaderRight>
      </Header>

      <TitleRow>
        <CalendarNav>
          <PageTitle>Agenda</PageTitle>
          <NavButton onClick={prevMonth}>&#8249;</NavButton>
          <MonthLabel>{monthLabel}</MonthLabel>
          <NavButton onClick={nextMonth}>&#8250;</NavButton>
          <GhostButton onClick={goToday} style={{ fontSize: "0.8rem", padding: "4px 12px" }}>
            Hoy
          </GhostButton>
        </CalendarNav>
        {isTherapist && (
          <PrimaryButton onClick={() => { setShowForm(!showForm); setFormError(""); }}>
            {showForm ? "Cancelar" : "Nueva cita"}
          </PrimaryButton>
        )}
      </TitleRow>

      {showForm && (
        <FormCard onSubmit={handleCreate}>
          <UserSearch
            selectedUser={formPatient}
            onSelect={setFormPatient}
            onClear={() => setFormPatient(null)}
          />
          <FormRow>
            <DateInput
              type="datetime-local"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              required
            />
            <PrimaryButton type="submit" disabled={formLoading}>
              {formLoading ? "Guardando..." : "Crear cita"}
            </PrimaryButton>
          </FormRow>
          {formError && <ErrorMsg>{formError}</ErrorMsg>}
        </FormCard>
      )}

      {loading ? (
        <EmptyState>Cargando...</EmptyState>
      ) : (
        <CalendarGrid>
          {DAY_NAMES.map((d) => (
            <DayHeader key={d}>{d}</DayHeader>
          ))}
          {calendarDays.map((day, i) => {
            const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
            const daySessions = sessionsByDate[key] || [];
            const isCurrentMonth = day.getMonth() === viewMonth;
            const isToday = sameDay(day, today);
            const visible = daySessions.slice(0, MAX_VISIBLE_EVENTS);
            const extra = daySessions.length - MAX_VISIBLE_EVENTS;

            return (
              <DayCell key={i} $isToday={isToday} $outside={!isCurrentMonth}>
                <DayNumber $isToday={isToday}>{day.getDate()}</DayNumber>
                {visible.map((s) => (
                  <EventChip
                    key={s.id}
                    $color={STATUS_COLORS[s.status]}
                    onClick={() => setSelectedSession(s)}
                    title={`${formatTime(s.scheduled_at)} — ${otherName(s)}`}
                  >
                    {formatTime(s.scheduled_at)} {otherName(s)}
                  </EventChip>
                ))}
                {extra > 0 && (
                  <MoreChip onClick={() => setDayDetailSessions(daySessions)}>
                    +{extra} más
                  </MoreChip>
                )}
              </DayCell>
            );
          })}
        </CalendarGrid>
      )}

      {dayDetailSessions && (
        <Overlay onClick={() => setDayDetailSessions(null)}>
          <DetailCard onClick={(e) => e.stopPropagation()}>
            <DetailTitle>
              Citas del {formatDate(dayDetailSessions[0]?.scheduled_at).split(",")[0]}
            </DetailTitle>
            {dayDetailSessions.map((s) => (
              <EventChip
                key={s.id}
                $color={STATUS_COLORS[s.status]}
                onClick={() => { setDayDetailSessions(null); setSelectedSession(s); }}
                style={{ whiteSpace: "normal" }}
              >
                {formatTime(s.scheduled_at)} — {otherName(s)}
                {" "}
                <StatusBadge color={STATUS_COLORS[s.status]} style={{ fontSize: "0.65rem", padding: "1px 6px" }}>
                  {STATUS_LABELS[s.status]}
                </StatusBadge>
              </EventChip>
            ))}
            <ActionRow>
              <GhostButton onClick={() => setDayDetailSessions(null)}>Cerrar</GhostButton>
            </ActionRow>
          </DetailCard>
        </Overlay>
      )}

      {selectedSession && (
        <Overlay onClick={() => setSelectedSession(null)}>
          <DetailCard onClick={(e) => e.stopPropagation()}>
            <DetailTitle>
              {isOwner(selectedSession) ? "Paciente" : "Terapeuta"}: {otherName(selectedSession)}
            </DetailTitle>
            <DetailRow>{formatDate(selectedSession.scheduled_at)}</DetailRow>
            <StatusBadge color={STATUS_COLORS[selectedSession.status]}>
              {STATUS_LABELS[selectedSession.status]}
            </StatusBadge>
            <ActionRow>
              {isOwner(selectedSession) && selectedSession.status === "scheduled" && (
                <PrimaryButton onClick={() => startSession(selectedSession)}>
                  Iniciar sesión
                </PrimaryButton>
              )}
              {isOwner(selectedSession) && selectedSession.status === "scheduled" && (
                <DangerButton onClick={() => cancelSession(selectedSession.id)}>
                  Cancelar
                </DangerButton>
              )}
              <GhostButton onClick={() => setSelectedSession(null)}>Cerrar</GhostButton>
            </ActionRow>
          </DetailCard>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default Agenda;
